// useLayout(): 'phone' | 'desktop'. Desktop when the window is >= 1000px wide or the app runs in a desktop shell
// (ARCHITECTURE §8). The cowork mini window (#/mini, K2) is its own route and ignores the layout.
import { useEffect, useState } from 'preact/hooks';
import { isDesktopShell } from './native';

export type Layout = 'phone' | 'desktop';
export const DESKTOP_MIN_WIDTH = 1000;

export function currentLayout(): Layout {
  if (typeof window === 'undefined') return 'phone';
  return isDesktopShell() || window.innerWidth >= DESKTOP_MIN_WIDTH ? 'desktop' : 'phone';
}

export function useLayout(): Layout {
  const [layout, setLayout] = useState<Layout>(currentLayout);
  useEffect(() => {
    const on = () => setLayout(currentLayout());
    window.addEventListener('resize', on);
    on();
    return () => window.removeEventListener('resize', on);
  }, []);
  return layout;
}

/** Reactive `prefers-reduced-motion` (also forced by ?motion=reduce for screenshots). */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  if (document.documentElement.dataset.reducedMotion === 'true') return true;
  return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useReducedMotion(): boolean {
  const [r, setR] = useState(prefersReducedMotion);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setR(prefersReducedMotion());
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return r;
}
